import { useEffect, useState } from "react";
import { Copy, Gift, Link2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export default function AdminBonus() {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("5");
  const [count, setCount] = useState("1");
  const [note, setNote] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/bonus-codes")
    .then(({ data }) => setData(data.data)).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const amt = parseFloat(amount), cnt = parseInt(count, 10);
    if (!(amt > 0 && amt <= 500) || !(cnt >= 1 && cnt <= 50)) {
      toast.error(t("bn.invalid")); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bonus-codes", { amount: amt, count: cnt, note: note.trim() || undefined,
        expires_days: expiry === "none" ? undefined : parseInt(expiry, 10) });
      toast.success(t("bn.created").replace("{n}", data.data.created.length));
      setNote("");
      load();
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const annulla = async (code) => {
    try {
      await api.delete(`/admin/bonus-codes/${code}`);
      toast.success(t("admin.saved"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "bonus_code_used" ? t("bn.used") : t("misc.error"));
    }
  };

  const _copyText = async (text, okMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg);
    } catch {
      // clipboard negato (permessi browser): selezione manuale via campo temporaneo
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success(okMsg);
      } catch {
        toast.info(text, { duration: 8000 });  // ultima spiaggia: testo nel toast, copia a mano
      }
      document.body.removeChild(ta);
    }
  };

  const copy = (code) => _copyText(code, code);

  const copyLink = (code) => {
    const link = `${window.location.origin}/r/${code}`;
    _copyText(link, t("bn.linkCopied"));
  };

  const inputCls = "rounded-full border border-slate-200 bg-transparent px-4 py-2 text-sm outline-none focus:border-[#FF6B35] transition-colors";

  return (
    <section data-testid="admin-bonus" className="mt-8 rounded-3xl border border-slate-200/80 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Gift className="w-4 h-4 text-[#FF6B35]" /> {t("bn.title")}
        </h2>
        {data && !data.error && (
          <div className="flex gap-2 font-mono-t text-[10px] font-bold uppercase tracking-widest">
            <span data-testid="bn-total-available" className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1">
              {data.totals.available} {t("bn.available")} · €{data.totals.value_available}
            </span>
            <span data-testid="bn-total-redeemed" className="rounded-full bg-slate-100 text-slate-500 px-3 py-1">
              {data.totals.redeemed} {t("bn.redeemed")}
            </span>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-5">{t("bn.hint")}</p>

      {/* creane di nuovi */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="flex gap-1">
          {[2, 5, 10].map((a) => (
            <button key={a} type="button" data-testid={`bn-amount-${a}`} onClick={() => setAmount(String(a))}
              className={`rounded-full px-4 py-2 font-mono-t text-[11px] font-bold transition-colors ${String(a) === amount ? "bg-[#FF6B35] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {a}€
            </button>
          ))}
        </div>
        <input data-testid="bn-amount-custom" type="number" min="1" max="500" step="0.5" value={amount}
          onChange={(e) => setAmount(e.target.value)} className={`${inputCls} w-24`} placeholder="€" />
        <span className="text-xs text-slate-400">×</span>
        <input data-testid="bn-count" type="number" min="1" max="50" value={count}
          onChange={(e) => setCount(e.target.value)} className={`${inputCls} w-20`} />
        <input data-testid="bn-note" type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={t("bn.note")} className={`${inputCls} flex-1 min-w-[160px]`} />
        <div className="flex gap-1" data-testid="bn-expiry">
          {[["none", t("bn.noExpiry")], ["30", t("bn.d30")], ["90", t("bn.d90")]].map(([v, label]) => (
            <button key={v} type="button" data-testid={`bn-expiry-${v}`} onClick={() => setExpiry(v)}
              className={`rounded-full px-3.5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${v === expiry ? "bg-[#0C0D0E] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>
        <button data-testid="bn-create" onClick={create} disabled={busy}
          className="rounded-full bg-[#0C0D0E] text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-50 flex items-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {t("bn.create")}
        </button>
      </div>

      {!data ? (
        <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#FF6B35]" /></div>
      ) : data.error ? (
        <p className="text-sm text-slate-400">{t("misc.error")}</p>
      ) : (
        <div className="space-y-1.5" data-testid="bn-list">
          {data.codes.length === 0 && <p className="py-4 text-center text-sm text-slate-400">{t("admin.noLogs")}</p>}
          {data.codes.map((c) => (
            <div key={c.code} data-testid={`bn-row-${c.code}`}
              className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-100 px-4 py-2.5">
              <button type="button" data-testid={`bn-copy-${c.code}`} onClick={() => copy(c.code)}
                title={t("bn.copy")}
                className="font-mono-t text-xs font-bold text-slate-800 hover:text-[#FF6B35] transition-colors flex items-center gap-1.5">
                <Copy className="w-3 h-3 text-slate-300" />{c.code}
              </button>
              <span className="font-display font-black text-sm text-[#E8551F]">{c.amount}€</span>
              {c.note && <span className="text-xs text-slate-400">{c.note}</span>}
              <span className="flex-1" />
              {c.redeemed_by ? (
                <span className="font-mono-t text-[10px] text-slate-400">
                  {t("bn.redeemedBy")} {c.redeemed_email} · {new Date(c.redeemed_at).toLocaleDateString(lang)}
                </span>
              ) : c.expired ? (
                <>
                  <span data-testid={`bn-expired-${c.code}`} className="rounded-full bg-slate-100 text-slate-400 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                    {t("bn.expired")}
                  </span>
                  <button type="button" data-testid={`bn-delete-${c.code}`} onClick={() => annulla(c.code)}
                    title={t("bn.annulla")}
                    className="text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  {c.expires_at && (
                    <span className="font-mono-t text-[10px] text-amber-600">
                      {t("bn.expiresOn")} {new Date(c.expires_at).toLocaleDateString(lang)}
                    </span>
                  )}
                  <button type="button" data-testid={`bn-copylink-${c.code}`} onClick={() => copyLink(c.code)}
                    title={t("bn.copyLink")}
                    className="text-slate-300 hover:text-[#FF6B35] transition-colors">
                    <Link2 className="w-3.5 h-3.5" />
                  </button>
                  <span className="rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                    {t("bn.available")}
                  </span>
                  <button type="button" data-testid={`bn-delete-${c.code}`} onClick={() => annulla(c.code)}
                    title={t("bn.annulla")}
                    className="text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
